import { Component, EventEmitter, Event, h, Prop, State, Method, Watch } from '@stencil/core';
import { NoAuth, hasBrowserSupport, type RemoteSigninResponse, type AuthenticateResponse } from '@noauth/browser';

type Step = 'signin' | 'otp' | 'qr';

export type AuthResponse = AuthenticateResponse & { email: string };

@Component({
  tag: 'noauth-login',
  styleUrl: 'noauth-login.css',
  // shadow: true,
})
export class NoauthLogin {
  /**
   * Project API key
   */
  @Prop() apiKey: string;

  /**
   * Default email address to sign in with
   */
  @Prop({ mutable: true, reflect: true }) email?: string;

  /**
   * Remember email in localStorage
   */
  @Prop() rememberEmail?: boolean = true;

  /**
   * Force authentication via QR code
   */
  @Prop() forceAuthQr?: boolean = false;

  /**
   * Force email verification
   */
  @Prop() forceEmailVerification?: boolean = false;

  /**
   * @ignore
   */
  @Prop() baseUrl?: string;

  @State() isLoading = false;
  @State() errorMessage: string | null = null;
  @State() qrCode: string | null = null;
  @State() otp: string | undefined = undefined;
  @State() step: Step = 'signin';

  /**
   * Emitted when the user is successfully authenticated
   */
  @Event() success: EventEmitter<AuthResponse>;

  private remoteSignin: RemoteSigninResponse;
  private emailInput: HTMLInputElement;
  private accessToken: string | null = null;
  private noauth: NoAuth;

  /**
   * Set the email address to sign in with
   */
  @Method()
  async setEmail(email: string) {
    this.email = email;
    if (this.rememberEmail) {
      localStorage.setItem('noauth-email', email ?? '');
    }
  }

  @Watch('email')
  handleEmailChange(newEmail: string) {
    if (this.rememberEmail) {
      localStorage.setItem('noauth-email', newEmail ?? '');
    }
  }

  componentWillLoad() {
    if (!this.apiKey) {
      this.isLoading = true;
      this.errorMessage = 'API key is required';
      return;
    }

    this.noauth = new NoAuth({
      apiKey: this.apiKey,
      baseURL: this.baseUrl,
    });

    if (this.rememberEmail) {
      const localEmail = localStorage.getItem('noauth-email');
      if (localEmail) {
        this.email = localEmail;
      }
    }
  }

  reset = (e?: Event) => {
    if (e) e.preventDefault();
    this.step = 'signin';
    this.qrCode = null;
    this.otp = undefined;
    this.isLoading = false;
    this.errorMessage = null;
    this.accessToken = null;
    this.remoteSignin?.cancel();
    this.emailInput.focus();
  };

  private submitOTP = async () => {
    this.step = 'otp';
    await this.noauth.otp.create(this.email);
  };

  handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    this.errorMessage = null;
    this.isLoading = true;

    try {
      // When submitted by account recovery button
      if (e.submitter?.classList.contains('account-recovery-btn')) {
        await this.submitOTP();
        return;
      }

      // Handle email verification
      if (this.accessToken && this.otp) {
        const emailVerified = await this.noauth.otp.verify(this.accessToken, this.otp);

        this.success.emit({
          email: this.email,
          verified: true,
          emailVerified,
          accessToken: this.accessToken,
        });
        return;
      }

      // Signin flow
      const browserSupport = await hasBrowserSupport();
      if (browserSupport && !this.forceAuthQr) {
        const { verified, emailVerified, accessToken } = await this.noauth.signin(this.email, this.otp);

        if (!verified) {
          return;
        }

        if (accessToken && !emailVerified && this.forceEmailVerification) {
          await this.submitOTP();
          if (accessToken) {
            this.accessToken = accessToken;
          }
          return;
        }

        this.success.emit({
          email: this.email,
          verified,
          emailVerified,
          accessToken,
        });
      } else {
        this.remoteSignin = await this.noauth.qr.create(this.email);
        this.qrCode = this.remoteSignin.qr;
        this.step = 'qr';

        const attempt = await this.remoteSignin.attempt;

        if (attempt.accessToken && !attempt.emailVerified && this.forceEmailVerification) {
          await this.submitOTP();
          if (attempt.accessToken) {
            this.accessToken = attempt.accessToken;
          }
          return;
        }

        this.success.emit({ email: this.email, ...attempt });
      }
    } catch (error: any) {
      this.reset(e);
      this.errorMessage = error?.message ?? 'Unexpected error';
    } finally {
      this.isLoading = false;
    }
  };

  render() {
    return (
      <form class="noauth-login" onSubmit={this.handleSubmit}>
        Sign in
        <span class={`message error ${this.errorMessage ? 'show' : 'hide'}`}>{this.errorMessage}</span>
        <input
          name="email"
          type="email"
          placeholder="Email"
          required
          value={this.email ?? ''}
          disabled={this.isLoading || this.step !== 'signin'}
          onChange={e => (this.email = (e.target as HTMLInputElement).value)}
          ref={el => (this.emailInput = el as HTMLInputElement)}
          autofocus
          autocomplete="webauthn"
        />
        {this.step === 'qr' && (
          <div class="noauth-step noauth-step-qr">
            {this.qrCode && <img src={this.qrCode} alt="QR Code" />}
            <button class="account-recovery-btn" onClick={this.reset}>
              Back
            </button>
          </div>
        )}
        {this.step === 'otp' && (
          <div class="noauth-step noauth-step-otp">
            <span>A 6 digit code has been sent to your email address. Please enter it below.</span>
            <input
              name="otp"
              type="tel"
              placeholder="Authorization code (6 digits)"
              required
              pattern="[0-9]{6}"
              minLength={6}
              maxLength={6}
              value={this.otp}
              disabled={this.isLoading}
              onChange={e => (this.otp = (e.target as HTMLInputElement).value)}
            />
            <button disabled={this.isLoading}>Continue</button>
            <button class="account-recovery-btn" onClick={this.reset} disabled={this.isLoading}>
              Back
            </button>
          </div>
        )}
        {this.step === 'signin' && (
          <div class="noauth-step noauth-step-signin">
            <button disabled={this.isLoading}>Continue</button>
            <button class="account-recovery-btn" disabled={this.isLoading} name="account-recovery">
              Recover account
            </button>
          </div>
        )}
      </form>
    );
  }
}
